import React, { useMemo, useCallback, forwardRef, useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import propertySchema from '@galo-monitor/forms/schemas/farm/property.schema'
import I18n, { useT } from '@galo-monitor/i18n'
import {
  getStatesOfCountry,
  getCitiesOfState,
  getCountryByCode,
  getStateByCodeAndCountry
} from '@galo-monitor/services/apis/countryStateCityApi'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import useGeolocation from '@galo-monitor/services/hooks/useGeolocation'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PropertyActions } from '@galo-monitor/stores/property'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import GoogleMaps from '@galo-monitor/web-components/Maps/Google'
import PopupMarker from '@galo-monitor/web-components/Maps/Google/components/PopupMarker'
import withGoogleMaps from '@galo-monitor/web-components/Maps/Google/hoc/withGoogleMaps'

import { Container, Label, Text } from './theme'

const UNIT_OF_MEASUREMENTS = [
  { id: '1', name: 'Hectare', initials: 'ha' },
  { id: '2', name: 'Acre', initials: 'acre' },
  { id: '3', name: 'Alqueire (SP)', initials: 'alsp' },
  { id: '4', name: 'Alqueire (MG)', initials: 'almg' }
]

const PropertyForm = withGoogleMaps(props => {
  const {
    property,
    loading,
    setLoading,
    onSuccess,
    onError,
    formRef,
    isGoogleMapsLoaded
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const mounted = useRef(true)

  const geolocation = useGeolocation()

  const [zoom, setZoom] = useState(property.lat && property.long ? 16 : 1)
  const [stateOptions, setStateOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [locationData, setLocationData] = useState({
    lat: isNil(property.lat) ? -4.897160 : +property.lat,
    lng: isNil(property.long) ? -17.666862 : +property.long
  })
  const [markerPosition, setMarkerPosition] = useState(locationData)

  const { data: countries } = useGaloMonitorApi('/shared/countries')

  const countryOptions = useMemo(
    () => map(countries, ({ name, isoCode }) => ({
      label: name,
      value: isoCode
    })),
    [countries]
  )

  const unitOfMeasurementsOptions = useMemo(
    () => map(UNIT_OF_MEASUREMENTS, ({ id, initials }) => ({
      label: t(`unit of measurement label ${ initials }`),
      value: id
    })),
    [t]
  )

  const updateLocationData = useCallback(
    (latitude, longitude) => {
      if (latitude && longitude)
        setLocationData({ lat: +latitude, lng: +longitude })
    },
    []
  )

  const updateMarkerLocationData = useCallback(
    (latitude, longitude) => {
      if (latitude && longitude)
        setMarkerPosition({ lat: +latitude, lng: +longitude })
    },
    []
  )

  const handleSubmit = useCallback(
    data => {
      setLoading(true)

      const selectedCountry = find(countries, country => country.isoCode === data.country)
      const selectedState = find(stateOptions, state => state.value === data.state)
      const { lat, lng: long } = markerPosition
      const coordinates = { lat, long }

      dispatch(PropertyActions.saveProperty(
        {
          ...property,
          ...data,
          ...coordinates,
          country_id: selectedCountry.id,
          state: selectedState.label
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('{this} account', {
              howMany: 1,
              this: t('property', { howMany: 1 })
            }),
            action: toLower(t(isEmpty(property) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))

          dispatch(
            PropertyActions.setCurrentPropertyUnitOfMeasurement(
              find(UNIT_OF_MEASUREMENTS, ({ id }) => data.measure_id === id)
            )
          )

          onSuccess()
        },
        onError
      ))
    },
    [setLoading, countries, stateOptions, markerPosition, dispatch, property, onError, snackbar, t, onSuccess]
  )

  const getStateIsoCodeByName = useCallback(
    propertyStateName => {
      if (property.country) {
        const countryStates = getStatesOfCountry(property.country.isoCode)
        const state = find(countryStates, ({ name: stateName }) => stateName === propertyStateName)
        return state?.isoCode ?? ''
      }
      return ''
    },
    [property.country]
  )

  const handleCountryChange = useCallback(
    async ({ target: { value } }, updateLocation = true) => {
      if (value) {
        const { latitude, longitude } = getCountryByCode(value)
        let data = getStatesOfCountry(value)
        if (mounted.current) {
          data = map(data, ({ name, isoCode }) => ({ label: name, value: isoCode }))
          setStateOptions(data)
          if (updateLocation) {
            updateLocationData(latitude, longitude)
            updateMarkerLocationData(latitude, longitude)
            setZoom(6)
          }
        }
      } else {
        setStateOptions([])
        formRef.current.clearField('state')
      }
      setCityOptions([])
      formRef.current.clearField('city')
    },
    [formRef, updateLocationData, updateMarkerLocationData]
  )

  const handleStateChange = useCallback(
    async ({ target: { value } }, updateLocation = true) => {
      if (value) {
        const countryCode = formRef.current.getFieldValue('country')
        const { latitude, longitude } = getStateByCodeAndCountry(countryCode, value)
        let data = getCitiesOfState(countryCode, value)
        if (mounted.current) {
          data = map(data, ({ name }) => ({ label: name, value: name }))
          setCityOptions(data)
          if (updateLocation) {
            updateLocationData(latitude, longitude)
            updateMarkerLocationData(latitude, longitude)
            setZoom(12)
          }
        }
      } else {
        setCityOptions([])
        formRef.current.clearField('city')
      }
    },
    [formRef, updateLocationData, updateMarkerLocationData]
  )

  const handleCityChange = useCallback(
    async ({ target: { value } }, stateIsoCode = null, updateLocation = true) => {
      if (value) {
        const countryCode = formRef.current.getFieldValue('country')
        const stateCode = stateIsoCode || formRef.current.getFieldValue('state')
        const cities = getCitiesOfState(countryCode, stateCode)
        if (countryCode && stateCode && mounted.current) {
          const selectedCity = find(cities, ({ name }) => name === value)
          const { latitude, longitude } = selectedCity
          if (updateLocation) {
            updateLocationData(latitude, longitude)
            updateMarkerLocationData(latitude, longitude)
            setZoom(16)
          }
        }
      }
    },
    [formRef, updateLocationData, updateMarkerLocationData]
  )

  const handleMarkerPositionChange = useCallback(
    ({ latLng }) => {
      const lat = latLng.lat()
      const lng = latLng.lng()

      setMarkerPosition({ lat, lng })
    },
    []
  )

  useEffect(() => {
    if (countries && property.country) {
      handleCountryChange({ target: { value: property.country.isoCode } }, false)
    }
  }, [countries, handleCountryChange, property.country])

  useEffect(() => {
    if (!isEmpty(stateOptions) && property.state) {
      const stateIsoCode = getStateIsoCodeByName(property.state)
      handleStateChange({ target: { value: stateIsoCode } }, false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateOptions])

  useEffect(() => {
    if (property.city) {
      const stateIsoCode = getStateIsoCodeByName(property.state)
      handleCityChange({ target: { value: property.city } }, stateIsoCode, false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityOptions])

  useEffect(() => () => {
    mounted.current = false
  }, [])

  useEffect(() => {
    const { error, latitude, longitude } = geolocation
    if (!error && !isNil(latitude) && !isNil(longitude)) {
      setLocationData({
        lat: latitude,
        lng: longitude
      })
    }
  }, [geolocation])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ propertySchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <InputText
              name="name"
              label={ t('name of property') }
              defaultValue={ property.name ?? '' }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="measure_id"
              label={ t('unit of measurement', { howMany: 1 }) }
              options={ unitOfMeasurementsOptions }
              defaultValue={ property.measureId?.toString() ?? '' }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="main_cultures"
              label={ t('main crops') }
              defaultValue={ property.mainCultures ?? '' }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="country"
              label={ t('country', { howMany: 1 }) }
              defaultValue={ property?.country?.isoCode ?? '' }
              onChange={ handleCountryChange }
              options={ countryOptions }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="state"
              label={ t('state', { howMany: 1 }) }
              defaultValue={ getStateIsoCodeByName(property.state) }
              disabled={ loading }
              onChange={ handleStateChange }
              options={ stateOptions }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="city"
              label={ t('city', { howMany: 1 }) }
              defaultValue={ property.city }
              disabled={ loading }
              onChange={ handleCityChange }
              options={ cityOptions }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 }>
            <Label>
              <I18n>location</I18n>
            </Label>
            <Text>
              <I18n>inform your location</I18n>.{' '}
              <I18n>location can be changed by dragging marker or clicking on map</I18n>.
            </Text>
            <GoogleMaps
              isLoaded={ isGoogleMapsLoaded }
              mapContainerStyle={ {
                width: '100%',
                height: 350
              } }
              center={ locationData }
              onClick={ handleMarkerPositionChange }
              zoom={ zoom }
            >
              {!isEmpty(markerPosition) ? (
                <PopupMarker position={ markerPosition } draggable>
                  <I18n>your farm location is here</I18n>
                </PopupMarker>
              ) : null}
            </GoogleMaps>
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

PropertyForm.propTypes = {
  property: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

PropertyForm.defaultProps = {
  property: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default forwardRef((props, formRef) => <PropertyForm formRef={ formRef } { ...props } />)
